import React, { Component } from "react";
import HelperClass from "../../../config/helperClass";
import { Link } from "react-router-dom";
import MUIDataTable from "mui-datatables";
import { tableLoader } from "../../../components/ContentLoaders/table_loader";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  getTrunks,
  getTrunkHosts,
  updateTrunkHost,
  deleteTrunkHost,
  getOutboundSBCIntegration,
} from "../../../config/reseller_api_calls";

const auth = new HelperClass();

const TYPE_LABELS = { 1: "Termination", 2: "Origination", 3: "Both" };

class AllTrunks extends Component {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this.state = {
      loading: false,
      errorMessage: "",
      successMessage: "",
      trunks: [],
      sipnav_account_ips: [],
      sipnav_carrier_ips: [],
      tableData: [],
      columnServices: [],

      // SBC IP modal
      showSbcIpModal: false,
      copiedPrimary: false,
      copiedBackup: false,

      // View hosts modal
      showHostsModal: false,
      selectedTrunk: null,
      hosts: [],
      hostsLoading: false,
      hostsError: "",
      hostsSuccessMessage: "",
      hostsActionError: "",

      // Edit host modal (shown instead of hosts modal)
      showEditModal: false,
      editHost: null,
      editForm: {
        ip_addr: "",
        port: 5060,
        direction: "inbound",
        type: 1,
        tech_prefix: "",
        enabled: 1,
      },
      editSaving: false,
      editError: "",
      editSuccess: "",

      // Delete confirm modal (shown instead of hosts modal)
      showDeleteModal: false,
      deletingHost: null,
      deleteInProgress: false,
    };
  }

  async componentDidMount() {
    this._isMounted = true;
    if (auth.isLogin() === false) {
      auth.logout();
    }
    this.setState({ columnServices: this.buildColumns() });
    this.loadTrunks();
    this.loadSipnavIPs();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  buildColumns = () => [
    {
      name: "Trunk ID",
      options: {
        customBodyRender: (value) => (
          <span className="text-muted" style={{ fontSize: "12px" }}>{value || "—"}</span>
        ),
      },
    },
    {
      name: "Trunk Name",
      options: {
        customBodyRender: (value) => (
          <span className="tb-lead">{value}</span>
        ),
      },
    },
    { name: "Client" },
    {
      name: "Channels",
      options: {
        customBodyRender: (value) => <span>{value?.toLocaleString()}</span>,
      },
    },
    {
      name: "Hosts",
      options: {
        customBodyRender: (value) => (
          <span className="badge badge-dim badge-primary">{value}</span>
        ),
      },
    },
    {
      name: "Status",
      options: {
        customBodyRender: (value) => {
          if (value === "active") {
            return <span className="badge badge-dim badge-success">Active</span>;
          } else if (value === "inactive") {
            return <span className="badge badge-warning">Inactive</span>;
          }
          return <span className="badge badge-secondary">{value}</span>;
        },
      },
    },
    {
      name: "Submitted",
      options: {
        customBodyRender: (value) => this.formatDate(value),
      },
    },
    {
      name: "Actions",
      options: {
        customBodyRender: (value, tableMeta) => {
          const trunk = this.state.trunks[tableMeta.rowIndex];
          return (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => this.openHostsModal(trunk)}
            >
              <em className="icon ni ni-eye mr-1"></em>
              View Hosts
            </button>
          );
        },
      },
    },
  ];

  loadTrunks = async () => {
    this.setState({ loading: true, errorMessage: "" });
    try {
      const response = await getTrunks(auth.getAccount(), auth.getToken(), "all");
      if (response.data.status === 200 && response.data.message === "success") {
        const trunks = response.data.data || [];
        const tableData = trunks.map((t) => [
          t.sipnav_trunk_id,
          t.trunk_name,
          t.client_name,
          t.channels,
          t.host_count || 0,
          t.status,
          t.submitted_date,
          "",
        ]);
        if (this._isMounted) {
          this.setState({ trunks, tableData, loading: false });
        }
      } else {
        if (this._isMounted) {
          this.setState({
            errorMessage: response.data.message || "Failed to load trunks",
            loading: false,
          });
        }
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({ errorMessage: "Error loading trunks.", loading: false });
      }
    }
  };

  loadSipnavIPs = async () => {
    try {
      const response = await getOutboundSBCIntegration(auth.getAccount(), auth.getToken());
      if (response.data.status === 200 && response.data.message === "success") {
        const data = response.data.data;
        const accountIps = data.account_ips
          ? data.account_ips.split(",").map((ip) => ip.trim())
          : [];
        const carrierIps = data.carrier_ips
          ? data.carrier_ips.split(",").map((ip) => ip.trim())
          : [];
        if (this._isMounted) {
          this.setState({ sipnav_account_ips: accountIps, sipnav_carrier_ips: carrierIps });
        }
      }
    } catch (error) {
      // non-critical
    }
  };

  // ─── Hosts modal ────────────────────────────────────────────────────────────

  openHostsModal = async (trunk) => {
    this.setState({
      showHostsModal: true,
      selectedTrunk: trunk,
      hosts: [],
      hostsLoading: true,
      hostsError: "",
    });
    try {
      const response = await getTrunkHosts(auth.getAccount(), auth.getToken(), trunk.id);
      if (response.data.status === 200) {
        if (this._isMounted) {
          this.setState({ hosts: response.data.data || [], hostsLoading: false });
        }
      } else {
        if (this._isMounted) {
          this.setState({ hostsError: response.data.message || "Failed to load hosts", hostsLoading: false });
        }
      }
    } catch {
      if (this._isMounted) {
        this.setState({ hostsError: "Error loading hosts.", hostsLoading: false });
      }
    }
  };

  closeHostsModal = () => {
    this.setState({ showHostsModal: false, selectedTrunk: null, hosts: [], hostsError: "", hostsSuccessMessage: "", hostsActionError: "" });
  };

  // ─── Edit host modal ─────────────────────────────────────────────────────────

  openEditModal = (host) => {
    // Close hosts modal, open edit modal — hosts data stays in state
    this.setState({
      showHostsModal: false,
      showEditModal: true,
      editHost: host,
      editForm: {
        ip_addr: host.ip_addr,
        port: host.port,
        direction: host.direction,
        type: host.type,
        tech_prefix: host.tech_prefix || "",
        enabled: host.enabled,
      },
      editError: "",
    });
  };

  closeEditModal = () => {
    // Cancel — go back to hosts modal without changes
    this.setState({ showEditModal: false, editHost: null, editError: "", showHostsModal: true });
  };

  handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === "checkbox" ? (checked ? 1 : 0) : value;
    // Tech prefix: strip non-digits, cap at 7 characters
    if (name === "tech_prefix") {
      finalValue = value.replace(/[^0-9]/g, "").slice(0, 7);
    }
    this.setState((prev) => ({
      editForm: { ...prev.editForm, [name]: finalValue },
    }));
  };

  saveEditHost = async () => {
    const { editHost, editForm } = this.state;

    // Validate IPv4
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
    const validIp = ipv4Regex.test(editForm.ip_addr) &&
      editForm.ip_addr.split(".").every((o) => Number(o) >= 0 && Number(o) <= 255);
    if (!validIp) {
      this.setState({ editError: "Please enter a valid IPv4 address (e.g. 34.51.220.120)" });
      return;
    }

    // Validate tech prefix: numbers only, max 7 digits
    if (editForm.tech_prefix && !/^\d{1,7}$/.test(editForm.tech_prefix)) {
      this.setState({ editError: "Tech Prefix must be numbers only and no more than 7 digits." });
      return;
    }

    this.setState({ editSaving: true, editError: "", editSuccess: "" });
    try {
      const response = await updateTrunkHost(
        auth.getAccount(),
        auth.getToken(),
        editHost.id,
        editForm
      );
      if (response.data.status === 200) {
        this.setState((prev) => ({
          hosts: prev.hosts.map((h) =>
            h.id === editHost.id ? response.data.data : h
          ),
          editSaving: false,
          editSuccess: "Host updated successfully",
        }));
        setTimeout(() => {
          if (this._isMounted) {
            this.setState({
              editSuccess: "",
              showEditModal: false,
              editHost: null,
              showHostsModal: true,
              hostsSuccessMessage: "Host updated successfully",
              hostsActionError: "",
            });
          }
        }, 1500);
      } else {
        this.setState({ editSaving: false, editError: response.data.message || "Update failed" });
      }
    } catch (err) {
      this.setState({ editSaving: false, editError: err?.response?.data?.message || "Error updating host." });
    }
  };

  // ─── Delete host modal ────────────────────────────────────────────────────────

  openDeleteModal = (host) => {
    // Close hosts modal, open delete confirm modal — hosts data stays in state
    this.setState({
      showHostsModal: false,
      showDeleteModal: true,
      deletingHost: host,
    });
  };

  cancelDelete = () => {
    // Cancel — go back to hosts modal
    this.setState({ showDeleteModal: false, deletingHost: null, showHostsModal: true });
  };

  deleteHost = async () => {
    const { deletingHost, selectedTrunk } = this.state;
    this.setState({ deleteInProgress: true });
    try {
      const response = await deleteTrunkHost(auth.getAccount(), auth.getToken(), deletingHost.id);
      if (response.data.status === 200) {
        this.setState((prev) => ({
          hosts: prev.hosts.filter((h) => h.id !== deletingHost.id),
          showDeleteModal: false,
          deletingHost: null,
          deleteInProgress: false,
          showHostsModal: true,
          hostsSuccessMessage: "Host deleted successfully",
          hostsActionError: "",
          trunks: prev.trunks.map((t) =>
            t.id === selectedTrunk.id
              ? { ...t, host_count: Math.max(0, (t.host_count || 1) - 1) }
              : t
          ),
          tableData: prev.tableData.map((row, i) =>
            prev.trunks[i] && prev.trunks[i].id === selectedTrunk.id
              ? [row[0], row[1], row[2], row[3], Math.max(0, (row[4] || 1) - 1), row[5], row[6], row[7]]
              : row
          ),
        }));
        setTimeout(() => this._isMounted && this.setState({ hostsSuccessMessage: "" }), 4000);
      } else {
        this.setState({
          deleteInProgress: false,
          showDeleteModal: false,
          deletingHost: null,
          showHostsModal: true,
          hostsActionError: response.data.message || "Delete failed",
          hostsSuccessMessage: "",
        });
      }
    } catch (err) {
      this.setState({
        deleteInProgress: false,
        showDeleteModal: false,
        deletingHost: null,
        showHostsModal: true,
        hostsActionError: err?.response?.data?.message || "Error deleting host.",
        hostsSuccessMessage: "",
      });
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  openSbcIpModal = () => {
    this.setState({ showSbcIpModal: true, copiedPrimary: false, copiedBackup: false });
  };

  closeSbcIpModal = () => {
    this.setState({ showSbcIpModal: false });
  };

  handleCopyPrimary = () => {
    this.setState({ copiedPrimary: true });
    setTimeout(() => this._isMounted && this.setState({ copiedPrimary: false }), 2000);
  };

  handleCopyBackup = () => {
    this.setState({ copiedBackup: true });
    setTimeout(() => this._isMounted && this.setState({ copiedBackup: false }), 2000);
  };

  getMuiTheme = () =>
    createTheme({
      overrides: {
        MUIDataTableHeadCell: {
          root: { padding: "4px 8px", fontWeight: "600" },
        },
      },
    });

  // ─── Render ───────────────────────────────────────────────────────────────────

  render() {
    const {
      trunks,
      loading,
      errorMessage,
      successMessage,
      sipnav_account_ips,
      sipnav_carrier_ips,
      tableData,
      columnServices,
      showSbcIpModal,
      copiedPrimary,
      copiedBackup,
      showHostsModal,
      selectedTrunk,
      hosts,
      hostsLoading,
      hostsError,
      hostsSuccessMessage,
      hostsActionError,
      showEditModal,
      editForm,
      editSaving,
      editError,
      editSuccess,
      showDeleteModal,
      deletingHost,
      deleteInProgress,
    } = this.state;

    const options = {
      filterType: "checkbox",
      responsive: "standard",
      selectableRows: "none",
      download: false,
      print: false,
      viewColumns: false,
      filter: false,
      search: true,
      pagination: true,
      rowsPerPage: 10,
      rowsPerPageOptions: [10, 25, 50, 100],
      elevation: 0,
      customToolbar: () => (
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={this.openSbcIpModal}
          style={{ marginRight: "10px" }}
        >
          <em className="icon ni ni-info-fill mr-1"></em>
          Our SBC IP Addresses
        </button>
      ),
    };

    return (
      <div className="nk-content nk-content-fluid">
        <div className="">
          <div className="nk-content-body">
            <div className="nk-block-head nk-block-head-sm">
              <div className="nk-block-between">
                <div className="nk-block-head-content">
                  <h3 className="nk-block-title page-title">Trunk Management</h3>
                  <div className="nk-block-des text-soft">
                    <p>Manage your SIP trunks and their hosts</p>
                  </div>
                </div>
                {trunks.length > 0 && (
                  <div className="nk-block-head-content">
                    <Link to="/add_trunk" className="btn btn-primary">
                      <em className="icon ni ni-plus mr-1"></em>
                      Create SIP Trunk
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {errorMessage && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                {errorMessage}
                <button
                  type="button"
                  className="close"
                  onClick={() => this.setState({ errorMessage: "" })}
                >
                  <span>&times;</span>
                </button>
              </div>
            )}

            {successMessage && (
              <div className="alert alert-success alert-dismissible fade show" role="alert">
                {successMessage}
                <button
                  type="button"
                  className="close"
                  onClick={() => this.setState({ successMessage: "" })}
                >
                  <span>&times;</span>
                </button>
              </div>
            )}

            {loading ? (
              <div className="nk-block">
                <div className="card card-bordered">
                  <div className="card-inner">{tableLoader()}</div>
                </div>
              </div>
            ) : trunks.length === 0 ? (
              <div className="nk-block">
                <div className="card card-bordered">
                  <div className="card-inner">
                    <div className="text-center py-5" style={{ padding: "100px 20px" }}>
                      <div className="mb-4">
                        <em
                          className="icon ni ni-server"
                          style={{
                            fontSize: "80px",
                            color: "#e5e9f2",
                            backgroundColor: "#f5f6fa",
                            borderRadius: "50%",
                            padding: "20px",
                          }}
                        ></em>
                      </div>
                      <h4 className="mb-2">No SIP Trunks Configured</h4>
                      <p className="text-soft mb-4" style={{ maxWidth: "500px", margin: "0 auto 20px" }}>
                        Set up a SIP trunk to connect your voice infrastructure.
                      </p>
                      <Link to="/add_trunk" className="btn btn-primary btn-lg">
                        <em className="icon ni ni-plus mr-2"></em>
                        Create Your First SIP Trunk
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="nk-block">
                <div className="card card-bordered">
                  <div className="card-inner">
                    <ThemeProvider theme={this.getMuiTheme()}>
                      <MUIDataTable
                        title="SIP Trunks"
                        data={tableData}
                        columns={columnServices}
                        options={options}
                      />
                    </ThemeProvider>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── SBC IP Modal ───────────────────────────────────────────────── */}
          {showSbcIpModal && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
              onClick={this.closeSbcIpModal}
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Our SBC IP addresses</h5>
                    <button type="button" className="close" onClick={this.closeSbcIpModal}>
                      <span>&times;</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    <p className="text-soft mb-4">
                      Safelist these IPs on your infrastructure to allow traffic through.
                    </p>
                    <div className="mb-4">
                      <label className="form-label text-uppercase text-muted" style={{ fontSize: "12px", fontWeight: "600" }}>
                        Primary IP{sipnav_account_ips.length > 1 ? "s" : ""}
                      </label>
                      {sipnav_account_ips.length > 0 ? (
                        sipnav_account_ips.map((ip, i) => (
                          <div key={i} className="mb-2" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <input type="text" className="form-control" value={ip} readOnly style={{ backgroundColor: "#f5f6fa", flex: "1 1 auto", minWidth: 0 }} />
                            <CopyToClipboard text={ip} onCopy={this.handleCopyPrimary}>
                              <button className="btn btn-outline-primary" type="button" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                                {copiedPrimary && i === 0 ? "Copied!" : "Copy"}
                              </button>
                            </CopyToClipboard>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted">No primary IPs configured</p>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label text-uppercase text-muted" style={{ fontSize: "12px", fontWeight: "600" }}>
                        Backup IP{sipnav_carrier_ips.length > 1 ? "s" : ""}
                      </label>
                      {sipnav_carrier_ips.length > 0 ? (
                        sipnav_carrier_ips.map((ip, i) => (
                          <div key={i} className="mb-2" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <input type="text" className="form-control" value={ip} readOnly style={{ backgroundColor: "#f5f6fa", flex: "1 1 auto", minWidth: 0 }} />
                            <CopyToClipboard text={ip} onCopy={this.handleCopyBackup}>
                              <button className="btn btn-outline-primary" type="button" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                                {copiedBackup && i === 0 ? "Copied!" : "Copy"}
                              </button>
                            </CopyToClipboard>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted">No backup IPs configured</p>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer bg-light justify-content-end">
                    <button type="button" className="btn btn-primary" onClick={this.closeSbcIpModal}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── View Hosts Modal ───────────────────────────────────────────── */}
          {showHostsModal && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
              onClick={this.closeHostsModal}
            >
              <div
                className="modal-dialog modal-dialog-centered modal-lg"
                role="document"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      Hosts — <span className="text-primary">{selectedTrunk && selectedTrunk.trunk_name}</span>
                    </h5>
                    <button type="button" className="close" onClick={this.closeHostsModal}>
                      <span>&times;</span>
                    </button>
                  </div>
                  <div className="modal-body" style={{ padding: "0" }}>
                    {hostsSuccessMessage && (
                      <div className="example-alert m-3 mb-0">
                        <div className="alert alert-pro alert-success">
                          <div className="alert-text">
                            <h4>Success</h4>
                            <p>{hostsSuccessMessage}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {hostsActionError && (
                      <div className="example-alert m-3 mb-0">
                        <div className="alert alert-pro alert-danger">
                          <div className="alert-text">
                            <h4>Error</h4>
                            <p>{hostsActionError}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {hostsLoading ? (
                      <div className="p-4">{tableLoader()}</div>
                    ) : hostsError ? (
                      <div className="alert alert-danger m-3">{hostsError}</div>
                    ) : hosts.length === 0 ? (
                      <div className="text-center p-5 text-muted">No hosts found for this trunk.</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-striped table-bordered mb-0">
                          <thead className="thead-light">
                            <tr>
                              <th>Host ID</th>
                              <th>IP Address</th>
                              <th>Port</th>
                              {/* <th>Direction</th> */}
                              <th>Type</th>
                              <th>Tech Prefix</th>
                              <th>Enabled</th>
                              <th style={{ minWidth: "130px" }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {hosts.map((host) => (
                              <tr key={host.id}>
                                <td className="text-muted"><strong>{host.display_id}</strong></td>
                                <td><strong>{host.ip_addr}</strong></td>
                                <td>{host.port}</td>
                                {/* <td>
                                  <span className={`badge badge-dim badge-${host.direction === "inbound" ? "info" : "warning"}`}>
                                    {host.direction}
                                  </span>
                                </td> */}
                                <td>{TYPE_LABELS[host.type] || host.type}</td>
                                <td>{host.tech_prefix || <span className="text-muted">—</span>}</td>
                                <td>
                                  {host.enabled ? (
                                    <span className="badge badge-success">Yes</span>
                                  ) : (
                                    <span className="badge badge-secondary">No</span>
                                  )}
                                </td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-outline-primary mr-1"
                                    onClick={() => this.openEditModal(host)}
                                  >
                                    <em className="icon ni ni-edit"></em>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => this.openDeleteModal(host)}
                                  >
                                    <em className="icon ni ni-trash"></em>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer bg-light justify-content-end">
                    <button type="button" className="btn btn-outline-gray" onClick={this.closeHostsModal}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Delete Confirm Modal ──────────────────────────────────────── */}
          {showDeleteModal && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}
            >
              <div className="modal-dialog modal-dialog-centered modal-sm" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Delete Host</h5>
                    <button type="button" className="close" onClick={this.cancelDelete} disabled={deleteInProgress}>
                      <span>&times;</span>
                    </button>
                  </div>
                  <div className="modal-body text-center py-4">
                    <em className="icon ni ni-alert-circle" style={{ fontSize: "48px", color: "#e85347" }}></em>
                    <h6 className="mt-3">Are you sure?</h6>
                    <p className="text-muted mb-0">
                      You are about to delete host{" "}
                      <strong>{deletingHost && deletingHost.ip_addr}</strong>.
                      <br />This action cannot be undone.
                    </p>
                  </div>
                  <div className="modal-footer justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-gray"
                      onClick={this.cancelDelete}
                      disabled={deleteInProgress}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={this.deleteHost}
                      disabled={deleteInProgress}
                    >
                      {deleteInProgress ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Edit Host Modal ────────────────────────────────────────────── */}
          {showEditModal && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1060 }}
              onClick={this.closeEditModal}
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Host</h5>
                    <button type="button" className="close" onClick={this.closeEditModal}>
                      <span>&times;</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    {editError && (
                      <div className="example-alert mb-3">
                        <div className="alert alert-pro alert-danger">
                          <div className="alert-text">
                            <h4>Error</h4>
                            <p>{editError}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {editSuccess && (
                      <div className="example-alert mb-3">
                        <div className="alert alert-pro alert-success">
                          <div className="alert-text">
                            <h4>Success</h4>
                            <p>{editSuccess}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="row g-3">
                      {/* Row 1: IP Address + Type side by side */}
                      <div className="col-7">
                        <div className="form-group">
                          <label className="form-label">IP Address</label>
                          <input
                            type="text"
                            className="form-control"
                            name="ip_addr"
                            value={editForm.ip_addr}
                            onChange={this.handleEditChange}
                            placeholder="0.0.0.0"
                          />
                        </div>
                      </div>
                      <div className="col-5">
                        <div className="form-group">
                          <label className="form-label">Type</label>
                          <select
                            className="form-control"
                            name="type"
                            value={editForm.type}
                            onChange={this.handleEditChange}
                          >
                            <option value={1}>Termination</option>
                            <option value={2}>Origination</option>
                            <option value={3}>Both</option>
                          </select>
                        </div>
                      </div>
                      {/* Row 2: Tech Prefix + Enabled side by side */}
                      <div className="col-8">
                        <div className="form-group">
                          <label className="form-label">Tech Prefix <small className="text-muted">(numbers only, max 7 digits)</small></label>
                          <input
                            type="text"
                            className="form-control"
                            name="tech_prefix"
                            value={editForm.tech_prefix}
                            onChange={this.handleEditChange}
                            placeholder="e.g. 123"
                            maxLength={7}
                          />
                        </div>
                      </div>
                      <div className="col-4 d-flex align-items-end">
                        <div className="form-group w-100">
                          <label className="form-label">Enabled</label>
                          <div className="custom-control custom-switch">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="editEnabled"
                              name="enabled"
                              checked={editForm.enabled === 1}
                              onChange={this.handleEditChange}
                            />
                            <label className="custom-control-label" htmlFor="editEnabled">
                              {editForm.enabled ? "Yes" : "No"}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer justify-content-end">
                    <button type="button" className="btn btn-outline-gray" onClick={this.closeEditModal}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={this.saveEditHost}
                      disabled={editSaving}
                    >
                      {editSaving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default AllTrunks;
